import {useQueries, useQuery} from "react-query";
import {
    ACHIEVEMENTS,
    ARTICLES,
    AWARD_POSITIONS,
    AWARDS,
    CATEGORIES,
    CITIES,
    CODES,
    COMMUNITY_ROUTES,
    GROUP_AIR_QUALITY,
    COUPONS,
    CUSTOMFIELDS,
    CYCLE_PATHS,
    DASH_ORGANIZATIONS,
    DASH_AWARDS,
    get,
    INVITE_FRIENDS,
    MESSAGES,
    OFFICES,
    ORGANIZATIONS,
    PARTIALS,
    POLYLINES,
    RANKINGS,
    RANKS,
    RECORDS,
    RIDES,
    SEATS,
    SESSIONS,
    SETTINGS,
    SHOPS,
    STATISTICS,
    SURVEIES,
    USERS,
    DASH_VENDORS,
    SPONSORED_NOTIFICATIONS
} from "./Client";
import {useEffect, useState} from "react";
import {db} from "../db";
import {useLiveQuery} from "dexie-react-hooks";
import dayjs from "dayjs";
import { FILTERS } from "constants/articles";
import { VISIBILITY_FILTERS } from "constants/vendors";

const ONE_DAY_IN_MILLIS = 24 * 60 * 60 * 1000;
 
 export const REFETCH_OPTIONS = {
         refetchOnWindowFocus: false,
         refetchOnMount: false,
         refetchOnReconnect: false,
         retry: false,
         staleTime: ONE_DAY_IN_MILLIS,
 };

export function useGetSessionsByOrganizationAndFilter(id, json) {
    const { status, data, error } = useQuery([SESSIONS, { id, json }], () => get(SESSIONS,
        { params: { org: id, json: json } }
    ));
    return { status, sessions: data, error };
}

export function useGetRidesByOrganizationAndFilter(id, json) {
    const { status, data, error } = useQuery([RIDES, { id, json }], () => get(RIDES + "/paginated",
        { params: { org: id, json: json } }
    ));
    return { status, rides: data, error };
}

export function useGetVendors(uid) {
    const {status, data, error} = useQuery([DASH_VENDORS, {vendor: uid}], () => get(DASH_VENDORS, {elem: uid}));
    return {status, vendors: data || [], error};
}

// Lista esercenti: paginata lato server, ritorna una Page. Filtri su businessName, city,
// province, vatNumber e address.
export function useGetVendorsPaged(json) {
    const {status, data, error} = useQuery([DASH_VENDORS, {json}], () => get(DASH_VENDORS, {params: {json}}));
    return {status, vendors: data, error};
}

export function useGetShops(uid, enabled) {
    const {
        status,
        data,
        error
    } = useQuery([SHOPS, {vendor: uid}], () => get(DASH_VENDORS, {elem: uid + '/' + SHOPS,}), {enabled: enabled || !!uid});
    return {status, shops: data || [], error};
}

export function useGetShopsByOrganization(oid) {
    const {
        status,
        data,
        error
    } = useQuery([SHOPS, {organization: oid}], () => get(`${ORGANIZATIONS}/${SHOPS}`, {params: {oid: oid}}));
    return {status, shops: data || [], error};
}

export function useGetParticipantShopsByOrganization(oid, isAll) {
    const {
        status,
        data,
        error
    } = useQuery([SHOPS, { organization: oid }], () => get(`${ORGANIZATIONS}/${oid}/participant-shops`),
        { enabled: !isAll });
    return { status, shops: data || [], error };
}

export function useGetShop(uid, shopId) {
    const {
        status,
        data,
        error
    } = useQuery([SHOPS, {shop: shopId}], () => get(DASH_VENDORS, {elem: uid + '/' + SHOPS + '/' + shopId}));
    return {status, shop: data || [], error};
}

export function useGetShopVerified(vid, shopId, enabled = true) {
    const {
        status,
        data,
        error
    } = useQuery([SHOPS, {shop: shopId}], () => get(`${SHOPS}/${shopId}/verify`, {params: {vid}}), {enabled});
    return {status, shop: data || {}, error};
}

export function useGetShopByPublicLink(publicLink, enabled = true) {
    const {
        status,
        data,
        error
    } = useQuery([SHOPS, {publicLink}], () => get(`${SHOPS}/publicLink`, {elem: publicLink}), {enabled});
    return {status, shop: data || {}, error};
}

export function useGetArticles(uid) {
    const {
        status,
        data,
        error
    } = useQuery([ARTICLES, {uid: uid}], () => get(DASH_VENDORS, {elem: uid + '/' + ARTICLES}), {enabled: !!uid});
    return {status, articles: data || [], error};
}

export function useGetArticle(aid, refetchOptions) {
    const {
        status,
        data,
        error
    } = useQuery([ARTICLES, {aid: aid}], () => get(ARTICLES, {elem: aid }), { ...refetchOptions });
    return {status, article: data || [], error};
}

export function useGetAllArticles({isAdmin, refetchOptions}) {
    const articleFiltersKeys = Object.keys(FILTERS);
    const vendorFiltersKeys = Object.keys(VISIBILITY_FILTERS);
    const {
        status,
        data,
        error
    } = useQuery(
        [ARTICLES, isAdmin],
        () =>
            get(`${ARTICLES}`,
                { params: {
                            articlesVisibility: articleFiltersKeys[FILTERS.all],
                            vendorVisibility: vendorFiltersKeys[VISIBILITY_FILTERS.all]
                }}
            ),
        { enabled: isAdmin, ...refetchOptions });
    return { status, articles: data || [], error };
}

export function useGetOfficeArticles(oid) {
    const {
        status,
        data,
        error
    } = useQuery([ARTICLES, {oid: oid}], () => get(ORGANIZATIONS, {elem: oid + '/office-articles'}), {enabled: !!oid});
    return {status, articles: data || [], error};
}

export function useGetArticleCoupons(id) {
    const {
        status,
        data,
        error
    } = useQuery([ARTICLES, {id: id}, COUPONS], () => get(ARTICLES + '/' + id + '/' + COUPONS));
    return {status, coupons: data || [], error};
}

export function useGetVendorCoupons(uid) {
    const {
        status,
        data,
        error
    } = useQuery([DASH_VENDORS, {uid: uid}, COUPONS], () => get(DASH_VENDORS + '/' + uid + '/' + COUPONS));
    return {status, coupons: data || [], error};
}

export function useGetCategories() {
    const {status, data, error} = useQuery(CATEGORIES, () => get(SHOPS, {elem: CATEGORIES}));
    return {status, categories: data || [], error};
}


export function useGetCoupons(organizationId = null) {
    const {
        status,
        data,
        error
    } = useQuery([COUPONS, {organization: organizationId}], () => get(ORGANIZATIONS + '/' + COUPONS, {params: {oid: organizationId}}));
    return {status, coupons: data || [], error};
}

export function useGetSessions() {
    const {status, data, error} = useQuery(SESSIONS, () => get(SESSIONS));
    return {status, sessions: data, error};
}

export function useGetIntervalSessions(start, end) {
    const {status, data, error} = useQuery([SESSIONS, {start, end}], () => get(SESSIONS, {
        params: {
            start,
            end
        }
    }), {enabled: !!start && !!end});
    return {status, sessions: data, error};
}

export function useGetSessionInfo(id, v2=false, partials=true) {
    const {status, data, error} = useQuery([SESSIONS, {id: id}], () => get(SESSIONS, {
        elem: id,
        params: {partials, v2}
    }), {enabled:!!id});
    return {status, session: data, error};
}

export function useGetPartialParameters() {
    const {status, data, error} = useQuery(SESSIONS + "DefaultParameters", () => get(SESSIONS + "/parameters"));
    return {status, defaultParameters: data || [], error};
}

export function useSynchronizeCities() {
    const {status, data, error} = useQuery(CITIES, () => get(CITIES), {staleTime: Infinity});
    const [state, setState] = useState({});

    useEffect(() => {
        db.cities.bulkPut(data)
            .then(() => setState({status, cities: data, error}))
            .catch(e => setState({error: e}))
    }, [status, data, error])
    return state
}

/**
 * restituisce una lista di città
 * @returns {{cities: (*|*[]|Array.<{istatId:int, city:string}>), error: unknown, status: "idle" | "error" | "loading" | "success"}}
 */
export function useGetCities() {
    return useLiveQuery(() => db.cities.toArray()) || []
}

export function useGetCity(istatId) {
    const {
        status,
        data,
        error
    } = useQuery([CITIES, {istatId: istatId}], () => get(CITIES, {elem: istatId}), {staleTime: Infinity});
    return {status, city: data || {}, error};
}

export function useGetOrganizations(enabled = true) {
    const {status, data, error} = useQuery(ORGANIZATIONS, () => get(ORGANIZATIONS), {enabled: enabled});
    return {status, organizations: data || [], error};
}

export function useGetOrganization(id, enabled) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}], () => get(ORGANIZATIONS, {elem: id}), {enabled: enabled});
    return {status, organization: data || {}, error};
}

export function useGetOrganizationOffices(oid) {
    const {
        status,
        data,
        error
    } = useQuery([OFFICES, {oid: oid}], () => get(`${ORGANIZATIONS}/${oid}/offices`), {enabled: !!oid});
    return {status, offices: data || [], error};
}

export function useGetOrganizationCodes(id) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, CODES], () => get(ORGANIZATIONS, {elem: id + "/" + CODES}));
    return {status, codes: data || [], error};
}

export function useGetOrganizationCommunityRoutes(id) {
    const {
        status,
        data,
        error
    } = useQuery([DASH_ORGANIZATIONS, {id: id}, COMMUNITY_ROUTES],
        () => get(DASH_ORGANIZATIONS, {elem: id + "/" + COMMUNITY_ROUTES}));
    return {status, communityRoutes: data || [], error};
}

// Qualità dell'aria di gruppo di un'iniziativa (tab admin). La pagina segue un evento in corso: i dati
// restano freschi 60 secondi e si rileggono al rientro nella tab, invece del giorno di REFETCH_OPTIONS.
const GROUP_AIR_QUALITY_OPTIONS = {...REFETCH_OPTIONS, staleTime: 60 * 1000, refetchOnMount: true};

// Giorni con sessioni UGO con dati ambientali.
export function useGetGroupAirQualityDays(oid) {
    const {status, data, error} = useQuery([DASH_ORGANIZATIONS, {id: oid}, GROUP_AIR_QUALITY, "days"],
        () => get(DASH_ORGANIZATIONS, {elem: oid + "/" + GROUP_AIR_QUALITY + "/days"}), GROUP_AIR_QUALITY_OPTIONS);
    return {status, days: data || [], error};
}

// Sessione comune di un giorno e finestra oraria (HH:mm nel fuso dell'iniziativa).
export function useGetGroupAirQualitySession(oid, day, from, to) {
    const {status, data, error} = useQuery([DASH_ORGANIZATIONS, {id: oid}, GROUP_AIR_QUALITY, "session", {day, from, to}],
        () => get(DASH_ORGANIZATIONS, {elem: oid + "/" + GROUP_AIR_QUALITY + "/session", params: {day, from, to}}),
        {...GROUP_AIR_QUALITY_OPTIONS, enabled: !!day});
    return {status, data, error};
}

export function useGetOrganizationSeats(id) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, SEATS], () => get(ORGANIZATIONS, {elem: id + "/" + SEATS}));
    return {status, seats: data || [], error};
}

export function useGetOrganizationManagers(id) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, USERS], () => get(ORGANIZATIONS, {elem: id + "/" + USERS}));
    return {status, managers: data || [], error};
}

export function useGetOrganizationMessages(id) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, MESSAGES], () => get(ORGANIZATIONS, {elem: id + "/" + MESSAGES}));
    return {status, messages: data || [], error};
}

export function useGetMessageDetails(id) {
    const {status, data, error} = useQuery([MESSAGES, {id: id}], () => get(MESSAGES, {elem: id}));
    return {status, message: data || [], error};
}

export function useGetOrganizationRanks(id) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, RANKINGS], () => get(ORGANIZATIONS, {elem: id + "/" + RANKINGS}));
    return {status, ranks: data || [], error};
}

export function useGetOrganizationCustomField(id) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, CUSTOMFIELDS], () => get(ORGANIZATIONS, {elem: id + "/" + CUSTOMFIELDS}));
    return {status, customField: data || [], error};
}

  export function useGetAllCustomFields() {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, CUSTOMFIELDS], () => get(`${ORGANIZATIONS}/${CUSTOMFIELDS}`));
    return {status, customFields: data || [], error};
}

export function useGetOrganizationAchievements(id) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, ACHIEVEMENTS], () => get(ORGANIZATIONS, {elem: id + "/" + ACHIEVEMENTS}));
    return {status, achievements: data || [], error};
}

export function useGetOrganizationSettings(id, options = {}) {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, {id: id}, SETTINGS], () => get(ORGANIZATIONS, {elem: id + "/" + SETTINGS}), {enabled: !!id, ...options});
    return {status, settings: data || [], error};
}

export function useGetOrganizationPartials(id, filters, enable, refetchOptions) {
    const {status, data, error} = useQuery([ORGANIZATIONS, {id: id}, PARTIALS, JSON.stringify(filters)],
        () => get(ORGANIZATIONS, {elem: id + "/" + PARTIALS, params: filters}),
        {
            enabled: enable, ...refetchOptions
        }
    );
    return {status, partials: data, error};
}

export function useGetOrganizationPartialsAll(id, filters, enable, refetchOptions) {
    const {status, data, error} = useQuery([PARTIALS, JSON.stringify(filters)],
        () => get("public/" + PARTIALS, {params: filters}),
        {
            enabled: enable, ...refetchOptions
        }
    );
    return { status, partials: data, error };
}

// !isAll
export function useGetOrganizationPolylines(id, filters, enable, refetchOptions) {
    const { status, data, error } = useQuery([ORGANIZATIONS, { id: id }, POLYLINES, JSON.stringify(filters)],
        () => get(ORGANIZATIONS, { elem: id + "/" + POLYLINES, params: filters }),
        {
            enabled: enable, ...refetchOptions
        }
        // { staleTime: Infinity },
    );
    return { status, polylines: data, error };
}

export function useGetOrganizationPolylinesAll(id, filters, enable, refetchOptions) {
    const {status, data, error} = useQuery([POLYLINES, JSON.stringify(filters)],
        () => get("public/" + POLYLINES, {params: filters}),
        // { staleTime: Infinity },
        {
            enabled: enable, ...refetchOptions
        }
    );
    return { status, polylines: data, error };
}

export function useGetOrganizationRecords(id) {
    const {status, data, error} = useQuery([ORGANIZATIONS, {id: id}, RECORDS],
        () => get(ORGANIZATIONS, {elem: id + "/" + RECORDS}));
    return {status, records: data || [], error};
}

export function useGetOrganizationAwardPositions(id) {
    const {status, data, error} = useQuery([ORGANIZATIONS, {id: id}, AWARD_POSITIONS],
        () => get(ORGANIZATIONS, {elem: id + "/" + AWARD_POSITIONS}));
    return {status, awardPositions: data || [], error};
}

export function useGetNationalRanks() {
    const {status, data, error} = useQuery(["National", RANKS], () => get(RANKINGS, {params: {"national": true}}));
    return {status, ranks: data || [], error};
}

export function useGetNationalAchievements() {
    const {
        status,
        data,
        error
    } = useQuery(["National", ACHIEVEMENTS], () => get(ACHIEVEMENTS, {params: {"national": true}}));
    return {status, achievements: data || [], error};
}

export function useGetRank(id, enabled = true) {
    const {status, data, error} = useQuery([RANKS, id], () => get(RANKINGS, {
        elem: id,
        params: {"withUsers": true}
    }, {enabled}));
    return {status, rank: data || {}, error};
}

export function useGetAchievement(id) {
    const {status, data, error} = useQuery([ACHIEVEMENTS, id], () => get(ACHIEVEMENTS, {elem: id + "/users"}));
    return {status, achievement: data || [], error};
}

export function useGetRankingAwards(rankingId) {
    const {status, data, error} = useQuery([AWARDS, {rid: rankingId}], () => get(`${RANKINGS}/${rankingId}/${AWARDS}`));
    return {status, awards: data || [], error};
}

export function useGetAchievementsAwards(achievementId, enabled = true) {
    const {
        status,
        data,
        error
    } = useQuery([AWARDS, {aid: achievementId}], () => get(`${ACHIEVEMENTS}/${achievementId}/${AWARDS}`), {enabled});
    return {status, awards: data || [], error};
}

export function getTableColumns(key, defaultColumns) {
    let colJson = localStorage.getItem(key);
    if (colJson) {
        let visibles = JSON.parse(colJson)
        let cols = defaultColumns.slice()
        cols.forEach(c => c.hide = (visibles.find(v => v.headerName === c.headerName) || {}).hide)
        return cols
    }
    return defaultColumns
}

export function useGetCustomAwards(json) {
    const {status, data, error} = useQuery(["AwardsCustom", {json}], () => get(`${DASH_AWARDS}/customs`, {params: {json}}));
    return {status, customAwards: data, error};
}

export function useGetSessionsStatistics(oid) {
    const {
        status,
        data,
        error
    } = useQuery(["SessionStatistics", {oid}], () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/sessions`), {staleTime: dayjs.duration(1, 'day').asMilliseconds()});//stale per un giorno
    return {status, sessionsStatistics: data || [], error};
}

export function useGetStatistics(oid, startDate, endDate, includeUsersWalletBalance, enableLagQuery = true, isAll) {
    return useQueries(
        [
            {
                queryKey: [STATISTICS, "sessions", oid, startDate, endDate, includeUsersWalletBalance],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/sessions`, {params: {startDate, endDate, includeUsersWalletBalance}}),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }//fresh per un giorno
            },
            {
                queryKey: [STATISTICS, "sessions-type", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/sessions-type`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            },
            {
                queryKey: [STATISTICS, "sessions-volume-hour", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/sessions-volume-hour`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            },
            {
                queryKey: [STATISTICS, "sessions-volume-day", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/sessions-volume-day`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            },
            {
                queryKey: [STATISTICS, "users-volume-age", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/users-volume-age`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            },
            {
                queryKey: [STATISTICS, "users-statistics", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/users-statistics`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            },
            {
                queryKey: [STATISTICS, "legs", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/legs`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled:  enableLagQuery && (!!startDate && !!endDate) && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }//fresh per un giorno
            }, {
                queryKey: [STATISTICS, "cycle-path", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/cycle-path`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            }, {
                queryKey: [STATISTICS, "wrong-way", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/wrong-way`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            }, {
                queryKey: [STATISTICS, "outside-urban", oid, startDate, endDate],
                queryFn: () => get(`${STATISTICS}/${ORGANIZATIONS}/${oid}/outside-urban`, {
                    params: {
                        startDate,
                        endDate
                    }
                }),
                enabled: !!startDate && !!endDate && !isAll,
                options: {
                    refetchOnWindowFocus: false,
                    refetchOnmount: false,
                    refetchOnReconnect: false,
                    staleTime: dayjs.duration(1, 'day').asMilliseconds()
                }
            },
        ],
    );
}

export function useGetStatisticsForAll(startDate, endDate, enableLagQuery = true, isAll) {
    return useQueries([
        {
            queryKey: [STATISTICS, "sessions", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/sessions`, {
                    params: { startDate, endDate },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            }, //fresh per un giorno
        },
        {
            queryKey: [STATISTICS, "sessions-type", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/sessions-type`, {
                    params: {
                        startDate,
                        endDate,
                    },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
        {
            queryKey: [STATISTICS, "sessions-volume-hour", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/sessions-volume-hour`, {
                    params: {
                        startDate,
                        endDate,
                    },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
        {
            queryKey: [STATISTICS, "sessions-volume-day", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/sessions-volume-day`, {
                    params: {
                        startDate,
                        endDate,
                    },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
        {
            queryKey: [STATISTICS, "users-volume-age", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/users-volume-age`, {
                    params: {
                        startDate,
                        endDate,
                    },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
        {
            queryKey: [STATISTICS, "users-statistics", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/users-statistics`, {
                    params: {
                        startDate,
                        endDate,
                    },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
        {
            queryKey: [STATISTICS, "legs", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/legs`, {
                    params: {
                        startDate,
                        endDate,
                    },
                }),
            enabled: enableLagQuery && !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            }, //fresh per un giorno
        },
        {
            queryKey: [STATISTICS, "cycle-path", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/cycle-path`, {
                    params: { startDate, endDate },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
        {
            queryKey: [STATISTICS, "wrong-way", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/wrong-way`, {
                    params: { startDate, endDate },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
        {
            queryKey: [STATISTICS, "outside-urban", startDate, endDate],
            queryFn: () =>
                get(`public/${STATISTICS}/outside-urban`, {
                    params: { startDate, endDate },
                }),
            enabled: !!startDate && !!endDate && !isAll,
            options: {
                refetchOnWindowFocus: false,
                refetchOnmount: false,
                refetchOnReconnect: false,
                staleTime: dayjs.duration(1, "day").asMilliseconds(),
            },
        },
    ]);
}

export function useGetVendorMessages(uid) {
    const {status, data, error} = useQuery([DASH_VENDORS, {uid}, MESSAGES], () => get(`${DASH_VENDORS}/${uid}/messages`));
    return {status, messages: data || [], error};
}

export function useGetVendorUnreadMessages(uid) {
    const {
        status,
        data,
        error
    } = useQuery([DASH_VENDORS, {uid}, "unreadMessages"], () => get(`${DASH_VENDORS}/${uid}/unread-messages`));
    return {status, messages: data || 0, error};
}

export function useGetArticleVisualizations(uid){
    const {
        status,
        data,
        error
    } = useQuery(["articlesVisualizations", { uid }], () => get(`${DASH_VENDORS}/${uid}/article-visualizations`));
    return { status, visualizations: data || [], error };
}

export function useGetPlafond(oid, type) {
    const {
        status,
        data,
        error
    } = useQuery(
        [ORGANIZATIONS, { oid, type }],
        () => get(`${ORGANIZATIONS}/${oid}/plafond`, { params: { type: type } })
    );
    return {status, plafond: data || [], error};
}

export function useGetSettings() {
    const {
        status,
        data,
        error
    } = useQuery([SETTINGS], () => get(SETTINGS));
    return {status, settings: data || [], error};
}

export function useGetAllSettings() {
    const {
        status,
        data,
        error
    } = useQuery([ORGANIZATIONS, "settings"], () => get(`${ORGANIZATIONS}/settings`));
    return {status, settings: data || [], error};
}

export function useGetInviteFriends(oid) {
    const {
        status,
        data,
        error
    } = useQuery([INVITE_FRIENDS, {oid}], () => get(`${INVITE_FRIENDS}/${oid}`));
    return {status, inviteFriends: data || [], error};
}

export function useGetSurveies(oid) {
    const {
        status,
        data,
        error
    } = useQuery([SURVEIES, {oid}], () => get(`${SURVEIES}?oid=${oid}`));
    return {status, surveies: data || [], error};
}

export function getCyclePaths(oids){
    return get(CYCLE_PATHS, { params: { oids: oids } });
}

export function useGetAllNotificationJobs({ enabled, refetchOptions }) {

    const { status, data, error } = useQuery(
        [MESSAGES, 'jobs', 'all'], 
        () => get(`${MESSAGES}/jobs`),
        { enabled, ...refetchOptions }
    );

    return { 
        status: status,
        notificationJobs: data || [],
        error: error
    };
}

// --- Sponsored Notifications ---

export function useGetSponsoredNotifications() {
    const { status, data, error, refetch } = useQuery(
        [SPONSORED_NOTIFICATIONS],
        () => get(SPONSORED_NOTIFICATIONS)
    );
    return { status, notifications: data || [], error, refetch };
}

export function useGetSponsoredNotification(id, enabled) {
    const { status, data, error } = useQuery(
        [SPONSORED_NOTIFICATIONS, { id }],
        () => get(SPONSORED_NOTIFICATIONS, { elem: id }),
        { enabled: enabled || !!id }
    );
    return { status, notification: data, error };
}

/** Coda di moderazione: solo admin, il backend rifiuta gli altri. */
export function useGetPendingSponsoredNotifications(enabled = true) {
    const { status, data, error, refetch } = useQuery(
        [SPONSORED_NOTIFICATIONS, 'moderation', 'pending'],
        () => get(SPONSORED_NOTIFICATIONS, { elem: 'moderation/pending' }),
        { enabled }
    );
    return { status, notifications: data || [], error, refetch };
}

/** Storico completo delle sponsorizzate, per vedere anche quelle gia' uscite. */
export function useGetAllSponsoredNotifications(enabled = true) {
    const { status, data, error, refetch } = useQuery(
        [SPONSORED_NOTIFICATIONS, 'moderation', 'all'],
        () => get(SPONSORED_NOTIFICATIONS, { elem: 'moderation' }),
        { enabled }
    );
    return { status, notifications: data || [], error, refetch };
}

export function useGetEstimateRecipients(targetType, latitude, longitude, radiusKm, enabled) {
    const { status, data, error } = useQuery(
        [SPONSORED_NOTIFICATIONS, 'estimate', { targetType, latitude, longitude, radiusKm }],
        () => get(SPONSORED_NOTIFICATIONS, {
            elem: 'estimate-recipients',
            params: { targetType, latitude, longitude, radiusKm }
        }),
        { enabled: !!enabled && !!targetType }
    );
    return { status, estimate: data, error };
}

// --- Portafoglio del negoziante ---

export function useGetVendorWallet(uid) {
    const {status, data, error} = useQuery(
        [DASH_VENDORS, {wallet: uid}],
        () => get(DASH_VENDORS, {elem: uid + '/wallet'}),
        {enabled: !!uid}
    );
    return {status, wallet: data, error};
}
